import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import { AdCampaignEventType } from '@/enums';
import {
  AdCampaignModuleType,
  Ads_DisplayAdContextQuery,
  Ads_DisplayAdContextQueryVariables,
  Ads_SampleRequestContextQuery,
  Ads_SampleRequestContextQueryVariables,
  DisplayAdVariantPackFieldsFragment,
  ItemsContext_VariantsPackFieldsFragment,
  PaidSearchEventData,
  SampleRequestFieldsFragment,
  SampleRequestVariantPackFieldsFragment,
} from '@/types';
import {
  AdImpressionSource,
  AdModuleType,
  DisplayAdModule,
  DisplayAdVPEventMap,
  ImpressionEvent,
  SampleRequestMap,
} from './ads-context-types';
import {
  DEFAULT_TIMEOUT_MINUTES,
  useImpressionContext,
} from './impression-context';
import {
  DISPLAY_AD_CONTEXT_QUERY,
  SAMPLE_REQUEST_CONTEXT_QUERY,
} from './ads-context-queries';
import { FocusSource, useComponentFocus } from '@/hooks/use-component-focus';
import { ItemListOption } from './item-context-types';
import { trackAdAnalytics } from '@/utils/ads';
import { useAuthenticatedUserContext } from './user-context';
import { useItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';
import { validate } from 'uuid';
import { ViewabilityConfig, ViewToken } from 'react-native';

export interface AdsContextType {
  displayAd: DisplayAdModule | null;
  displayAdLoading: boolean;
  latestSampleRequest: SampleRequestFieldsFragment | null;
  onViewableAdItemsChanged: (
    viewableItems: ViewToken[],
    source: AdImpressionSource,
    campaignUUIDOverride?: string,
  ) => void;
  sampleRequestMap: SampleRequestMap;
}

interface Props {
  children?: React.ReactNode;
}

export const AD_VIEWABILITY_CONFIG: ViewabilityConfig = {
  itemVisiblePercentThreshold: 40,
};

export const AdsContextProvider: React.FC<Props> = ({ children }) => {
  const itemQueryVariables = useItemQueryVariables();
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
    user,
  } = useAuthenticatedUserContext();
  const { fireDedupedImpressionEvent } = useImpressionContext();

  const variables = useMemo(
    () =>
      restaurantUUID && supplierUUID
        ? {
            ...itemQueryVariables,
            restaurantUUID,
            supplierUUID,
          }
        : null,
    [itemQueryVariables, restaurantUUID, supplierUUID],
  );

  const isOfflineModeActive = useOfflineModeActive();

  const {
    data: displayAdContextData,
    loading: displayAdLoading,
    refetch: displayAdRefetch,
  } = useWrappedQuery<
    Ads_DisplayAdContextQuery,
    Ads_DisplayAdContextQueryVariables
  >(DISPLAY_AD_CONTEXT_QUERY, {
    context: {
      noBatch: true,
    },
    fetchPolicy: 'network-only',
    skip: !variables || isOfflineModeActive,
    variables: variables!,
  });

  const { data: sampleRequestContextData, refetch: sampleRequestRefetch } =
    useWrappedQuery<
      Ads_SampleRequestContextQuery,
      Ads_SampleRequestContextQueryVariables
    >(SAMPLE_REQUEST_CONTEXT_QUERY, {
      context: {
        noBatch: true,
      },
      fetchPolicy: 'network-only',
      skip: !variables || isOfflineModeActive,
      variables: variables!,
    });

  // Refetch whenever the app receives focus
  useComponentFocus({
    onFocus: useCallback(
      async (type: FocusSource) => {
        if (type === FocusSource.App && variables) {
          try {
            await Promise.all([displayAdRefetch(), sampleRequestRefetch()]);
          } catch (e) {
            // Do nothing
          }
        }
      },
      [displayAdRefetch, sampleRequestRefetch, variables],
    ),
  });

  const displayAd: DisplayAdModule | null = useMemo(() => {
    if (isOfflineModeActive) {
      return null;
    }

    if (!displayAdContextData?.getDisplayAdVariantPacksEvents.length) {
      return null;
    }

    const mappedDisplayAdVPData =
      displayAdContextData.getDisplayAdVariantPacksEvents.reduce<{
        variantPackEventMap: DisplayAdVPEventMap;
        variantPacks: ItemsContext_VariantsPackFieldsFragment[];
      }>(
        (acc, response) => {
          const variantPackUUID = response.variant_pack!.uuid;
          acc.variantPackEventMap[variantPackUUID] = response;
          acc.variantPacks.push(response.variant_pack!);

          return acc;
        },
        {
          variantPackEventMap: {},
          variantPacks: [],
        },
      );

    return {
      // UUIDs and content values are always the same per variant pack
      adManagerUUID:
        displayAdContextData.getDisplayAdVariantPacksEvents[0].ad_manager_id,
      advertiserUUID:
        displayAdContextData.getDisplayAdVariantPacksEvents[0].advertiser_id,
      campaignUUID:
        displayAdContextData.getDisplayAdVariantPacksEvents[0].ad_campaign_id,
      content:
        displayAdContextData.getDisplayAdVariantPacksEvents[0]
          .display_ad_content,
      ...mappedDisplayAdVPData,
    };
  }, [
    displayAdContextData?.getDisplayAdVariantPacksEvents,
    isOfflineModeActive,
  ]);

  const [sampleRequestMap, latestSampleRequest]: [
    SampleRequestMap,
    SampleRequestFieldsFragment | null,
  ] = useMemo(() => {
    if (isOfflineModeActive) {
      return [{}, null];
    }

    let lastSampleRequest: SampleRequestFieldsFragment | null = null;

    const requestMap = (
      sampleRequestContextData?.getSampleRequestVariantPacks || []
    ).reduce<SampleRequestMap>((acc, data) => {
      const variantPackUUID = data.variant_pack_id;
      acc[variantPackUUID] = data;

      const previousRequest = data.sample_request;
      if (
        previousRequest &&
        previousRequest.requested_at &&
        (!lastSampleRequest ||
          previousRequest.requested_at > lastSampleRequest.requested_at)
      ) {
        lastSampleRequest = previousRequest;
      }

      return acc;
    }, {});

    return [requestMap, lastSampleRequest];
  }, [
    sampleRequestContextData?.getSampleRequestVariantPacks,
    isOfflineModeActive,
  ]);

  const userUUID = user.uuid;
  const fireAdsImpressionEvent = useCallback(
    ({
      adEventData,
      campaignUUID,
      impressionSource,
      type,
      variantPackUUID,
    }: ImpressionEvent) => {
      const impressionEventKey = [
        campaignUUID,
        impressionSource,
        type,
        variantPackUUID,
      ].join('-');

      fireDedupedImpressionEvent(
        impressionEventKey,
        () => {
          trackAdAnalytics({
            adCampaignUUID: campaignUUID,
            adManagerUUID: adEventData.adManagerUUID,
            adModule: adEventData.adModule,
            advertiserUUID: adEventData.advertiserUUID,
            distributorUUID: supplierUUID,
            eventType: AdCampaignEventType.IMPRESSION,
            eventUUID: adEventData.eventUUID,
            gtin: adEventData.gtin,
            keyword: adEventData.keyword ?? '',
            operatorUUID: restaurantUUID,
            source: impressionSource,
            userUUID,
            variantPackUUID: validate(variantPackUUID) ? variantPackUUID : '',
          });
        },
        DEFAULT_TIMEOUT_MINUTES,
      );
    },
    [fireDedupedImpressionEvent, restaurantUUID, supplierUUID, userUUID],
  );

  const onViewableAdItemsChangedRef = useRef(
    (viewableItems: ViewToken[], impressionSource: AdImpressionSource) => {
      for (const viewableItem of viewableItems) {
        let displayAdData: DisplayAdVariantPackFieldsFragment | undefined;
        let paidSearch: PaidSearchEventData | undefined;
        let sampleRequest: SampleRequestVariantPackFieldsFragment | undefined;
        let variantPackUUID: string | undefined;

        for (const sku of (viewableItem.item as ItemListOption).metadata
          ?.skus || []) {
          displayAdData = displayAdData ?? sku.displayAd;
          paidSearch = paidSearch ?? sku.paidSearch;
          sampleRequest = sampleRequest ?? sku.sampleRequest;

          if (displayAdData || paidSearch || sampleRequest) {
            variantPackUUID = variantPackUUID ?? sku.variantPackUUID;
          }
        }

        if (variantPackUUID) {
          if (displayAdData) {
            fireAdsImpressionEvent({
              adEventData: {
                adManagerUUID: displayAdData.ad_manager_id,
                adModule: AdCampaignModuleType.DisplayAd,
                advertiserUUID: displayAdData.advertiser_id,
                eventUUID:
                  displayAdData.variant_pack_event_data.impression_event_id,
                gtin: displayAdData.gtin,
                source: impressionSource,
              },
              campaignUUID: displayAdData.ad_campaign_id,
              impressionSource,
              type: AdModuleType.DisplayAd,
              variantPackUUID,
            });
          }
          if (paidSearch) {
            fireAdsImpressionEvent({
              adEventData: {
                adManagerUUID: paidSearch.ad_manager_id,
                adModule: AdCampaignModuleType.PaidSearch,
                advertiserUUID: paidSearch.advertiser_id,
                eventUUID: paidSearch.impression_event_id,
                gtin: paidSearch.gtin,
                keyword: paidSearch.matching_keyword,
                source: impressionSource,
              },
              campaignUUID: paidSearch.ad_campaign_id,
              impressionSource,
              type: AdModuleType.PaidSearch,
              variantPackUUID,
            });
          }
          if (sampleRequest) {
            fireAdsImpressionEvent({
              adEventData: {
                adManagerUUID: sampleRequest.ad_manager_id,
                adModule: AdCampaignModuleType.SampleRequest,
                advertiserUUID: sampleRequest.advertiser_id,
                eventUUID:
                  sampleRequest.sample_request_event_data.impression_event_id,
                gtin: sampleRequest.gtin,
                source: impressionSource,
              },
              campaignUUID: sampleRequest.ad_campaign_id,
              impressionSource,
              type: AdModuleType.SampleRequest,
              variantPackUUID,
            });
          }
        }
      }
    },
  );

  const value = useMemo(() => {
    return {
      displayAd,
      displayAdLoading,
      latestSampleRequest,
      onViewableAdItemsChanged: onViewableAdItemsChangedRef.current,
      sampleRequestMap,
    };
  }, [displayAd, displayAdLoading, latestSampleRequest, sampleRequestMap]);

  return <AdsContext.Provider value={value}>{children}</AdsContext.Provider>;
};

export const DummyAdsContextProvider: React.FC<Props> = ({ children }) => {
  const value = useMemo(() => {
    return {
      displayAd: null,
      displayAdLoading: false,
      latestSampleRequest: null,
      onViewableAdItemsChanged: () => {},
      sampleRequestMap: {},
    };
  }, []);
  return <AdsContext.Provider value={value}>{children}</AdsContext.Provider>;
};

export const AdsContext = createContext<AdsContextType | null>(null);

export const useAdsContext = () => {
  const context = useContext(AdsContext);
  if (!context) {
    throw new Error('useAdsContext requires a AdsContextProvider');
  }

  return context;
};
